import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import dayjs from 'dayjs'

// O backend recebe `LocalDateTime` (sem fuso): `toISOString()` mandaria UTC com Z e deslocaria
// a janela do filtro em 3h (mesmo bug corrigido no ReuniaoDialog em 02/09).
const LOCAL_DATE_TIME = 'YYYY-MM-DDTHH:mm:ss'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { authStore } from '@/store/authStore'
import { auditoriaService } from '@/services/auditoriaService'
import { EventoAuditoria } from '@/types/auditoria'

const ACOES_CONHECIDAS = [
  'CREATE',
  'UPDATE',
  'DELETE',
  'INATIVAR',
  'MUDANCA_ESTAGIO',
  'ESTRUTURACAO',
  'DEFESA_CREDITO',
  'LOGIN',
  'LOGIN_FAILED',
]

const acaoColor = (acao: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
  if (acao === 'CREATE') return 'success'
  if (acao === 'DELETE' || acao === 'INATIVAR' || acao === 'LOGIN_FAILED') return 'error'
  if (acao === 'UPDATE' || acao === 'MUDANCA_ESTAGIO' || acao === 'ESTRUTURACAO' || acao === 'DEFESA_CREDITO')
    return 'warning'
  if (acao === 'LOGIN') return 'info'
  return 'default'
}

const AuditoriaPage: FC = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const isAdmin = useMemo(
    () => !!usuario?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN'),
    [usuario]
  )

  useEffect(() => {
    if (usuario && !isAdmin) {
      enqueueSnackbar('Auditoria é restrita a administradores', { variant: 'warning' })
      navigate('/dashboard')
    }
  }, [usuario, isAdmin, navigate, enqueueSnackbar])

  const [eventos, setEventos] = useState<EventoAuditoria[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const [page, setPage] = useState(0)
  const [size, setSize] = useState(50)

  const [modulo, setModulo] = useState('')
  const [recurso, setRecurso] = useState('')
  const [acao, setAcao] = useState('')
  const [inicio, setInicio] = useState('')
  const [fim, setFim] = useState('')

  const [detalhe, setDetalhe] = useState<EventoAuditoria | null>(null)

  const carregar = useCallback(async () => {
    if (!isAdmin) return
    setLoading(true)
    setErro(null)
    try {
      const resp = await auditoriaService.listar({
        page,
        size,
        modulo: modulo || undefined,
        recurso: recurso || undefined,
        acao: acao || undefined,
        inicio: inicio ? dayjs(inicio).startOf('day').format(LOCAL_DATE_TIME) : undefined,
        fim: fim ? dayjs(fim).endOf('day').format(LOCAL_DATE_TIME) : undefined,
      })
      setEventos(resp.content ?? [])
      setTotal(resp.totalElements ?? 0)
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao listar eventos')
      setEventos([])
    } finally {
      setLoading(false)
    }
  }, [isAdmin, page, size, modulo, recurso, acao, inicio, fim])

  useEffect(() => {
    carregar()
  }, [carregar])

  if (!isAdmin) return null

  return (
    <Box>
      <PageHeader
        title="Auditoria"
        subtitle="Trilha completa das operações do sistema"
        action={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={async () => {
                try {
                  const blob = await auditoriaService.exportarCsv({
                    modulo: modulo || undefined,
                    recurso: recurso || undefined,
                    acao: acao || undefined,
                    inicio: inicio ? dayjs(inicio).startOf('day').format(LOCAL_DATE_TIME) : undefined,
                    fim: fim ? dayjs(fim).endOf('day').format(LOCAL_DATE_TIME) : undefined,
                    size: 10000,
                  })
                  const url = URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `auditoria-${dayjs().format('YYYY-MM-DD')}.csv`
                  document.body.appendChild(a)
                  a.click()
                  a.remove()
                  setTimeout(() => URL.revokeObjectURL(url), 60_000)
                } catch (e) {
                  enqueueSnackbar(
                    e instanceof Error ? e.message : 'Falha ao exportar CSV',
                    { variant: 'error' }
                  )
                }
              }}
              disabled={loading}
            >
              Exportar evidência
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={carregar} disabled={loading}>
              Atualizar
            </Button>
          </Stack>
        }
      />

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {/* KPIs (Onda 2 / A05) — "Eventos" cita a MESMA consulta paginada da tabela;
          o backend de auditoria não tem agregação de sucesso/alertas/tenants → "—"
          (lacuna relatada no plano). */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            variante="escura"
            label="Eventos"
            carregando={loading}
            valor={total}
            legenda="Total do filtro atual — mesma fonte da tabela"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Sucesso" valor="—" legenda="Sem agregação no backend" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Alertas" valor="—" legenda="Sem agregação no backend" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Tenants" valor="—" legenda="Evento não carrega tenant (lacuna)" />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            size="small"
            label="Módulo"
            value={modulo}
            onChange={(e) => setModulo(e.target.value)}
            placeholder="originei-clientes"
            sx={{ minWidth: 200 }}
          />
          <TextField
            size="small"
            label="Recurso"
            value={recurso}
            onChange={(e) => setRecurso(e.target.value)}
            placeholder="Cliente, Oportunidade…"
            sx={{ minWidth: 180 }}
          />
          <TextField
            size="small"
            select
            label="Ação"
            value={acao}
            onChange={(e) => setAcao(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Todas</MenuItem>
            {ACOES_CONHECIDAS.map((a) => (
              <MenuItem key={a} value={a}>
                {a}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="De"
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="Até"
            value={fim}
            onChange={(e) => setFim(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              {/* Colunas do Figma 28:640: Evento · Ator · Tenant · Resultado. QUANDO
                  fica — evidência sem data não é evidência. */}
              <TableRow>
                <TableCell><strong>QUANDO</strong></TableCell>
                <TableCell><strong>EVENTO</strong></TableCell>
                <TableCell><strong>ATOR</strong></TableCell>
                <TableCell><strong>TENANT</strong></TableCell>
                <TableCell><strong>RESULTADO</strong></TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : eventos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum evento de auditoria encontrado.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                eventos.map((ev) => (
                  <TableRow key={ev.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">
                        {dayjs(ev.ocorridoEm).format('DD/MM/YYYY')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(ev.ocorridoEm).format('HH:mm:ss')}
                      </Typography>
                    </TableCell>
                    {/* EVENTO = ação + recurso/módulo — o que aconteceu, num olhar. */}
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={ev.acao} size="small" color={acaoColor(ev.acao)} />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {ev.recurso}
                            {ev.recursoId && (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontFamily: 'monospace', ml: 0.5 }}
                              >
                                {ev.recursoId.slice(0, 8)}…
                              </Typography>
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {ev.modulo}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{ev.usuarioEmail ?? '—'}</Typography>
                      {ev.usuarioId && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {ev.usuarioId.slice(0, 8)}…
                        </Typography>
                      )}
                    </TableCell>
                    {/* O evento (V1–V3) ainda não carrega tenant nem campo de resultado
                        — "—" até a coluna existir; lacuna relatada no plano. */}
                    <TableCell>—</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="primary"
                        onClick={() => setDetalhe(ev)}
                        sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}
                      >
                        Abrir →
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={size}
          onRowsPerPageChange={(e) => {
            setSize(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />
      </Paper>

      {/* Drawer de evidência (Figma 28:640): IP/dispositivo/correlação/antes-depois. */}
      <Drawer
        anchor="right"
        open={!!detalhe}
        onClose={() => setDetalhe(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 3 } }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Detalhes do evento
        </Typography>
        {detalhe && (
          <Typography component="div" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {dayjs(detalhe.ocorridoEm).format('DD/MM/YYYY HH:mm:ss')} ·{' '}
            <strong>{detalhe.modulo}</strong> · {detalhe.recurso}
          </Typography>
        )}
        <Box>
          {detalhe && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Ação</Typography>
                <Box>
                  <Chip label={detalhe.acao} size="small" color={acaoColor(detalhe.acao)} />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Recurso</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {detalhe.recurso}
                  {detalhe.recursoId && (
                    <Typography
                      component="span"
                      sx={{ ml: 1, fontFamily: 'monospace', color: 'text.secondary' }}
                    >
                      {detalhe.recursoId}
                    </Typography>
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Usuário</Typography>
                <Typography variant="body2">
                  {detalhe.usuarioEmail ?? '—'}{' '}
                  {detalhe.usuarioId && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ ml: 1, fontFamily: 'monospace', color: 'text.secondary' }}
                    >
                      {detalhe.usuarioId}
                    </Typography>
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Descrição</Typography>
                <Typography variant="body2">{detalhe.descricao ?? '—'}</Typography>
              </Box>
              {detalhe.detalhes && Object.keys(detalhe.detalhes).length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Detalhes (payload)</Typography>
                  <Paper variant="outlined" sx={{ p: 2, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(detalhe.detalhes, null, 2)}
                  </Paper>
                </Box>
              )}

              {(detalhe.payloadAntes || detalhe.payloadDepois) && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Diff antes / depois</Typography>
                  {(() => {
                    const antes = (detalhe.payloadAntes ?? {}) as Record<string, unknown>
                    const depois = (detalhe.payloadDepois ?? {}) as Record<string, unknown>
                    const chaves = Array.from(new Set([...Object.keys(antes), ...Object.keys(depois)]))
                    const alterados = chaves.filter((k) => JSON.stringify(antes[k]) !== JSON.stringify(depois[k]))
                    if (alterados.length === 0) {
                      return (
                        <Typography variant="caption" color="text.secondary">
                          Nenhum campo alterado.
                        </Typography>
                      )
                    }
                    return (
                      <Paper variant="outlined" sx={{ mt: 0.5 }}>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '180px 1fr 1fr',
                            fontSize: 12,
                            '& > div': {
                              p: 1,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              fontFamily: 'monospace',
                              wordBreak: 'break-word',
                            },
                          }}
                        >
                          <Box sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>CAMPO</Box>
                          <Box sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>ANTES</Box>
                          <Box sx={{ fontWeight: 700, bgcolor: 'grey.100' }}>DEPOIS</Box>
                          {alterados.flatMap((k) => [
                            <Box key={`${k}-k`} sx={{ fontWeight: 600 }}>{k}</Box>,
                            <Box key={`${k}-a`} sx={{ bgcolor: 'rgba(231,76,60,0.06)' }}>
                              {antes[k] === undefined ? '—' : JSON.stringify(antes[k])}
                            </Box>,
                            <Box key={`${k}-d`} sx={{ bgcolor: 'rgba(39,174,96,0.06)' }}>
                              {depois[k] === undefined ? '—' : JSON.stringify(depois[k])}
                            </Box>,
                          ])}
                        </Box>
                      </Paper>
                    )
                  })()}
                </Box>
              )}

              {/* request_context (V2 da auditoria) traz IP e User-Agent; tenant e
                  correlação não existem no evento — mostramos o que existe, "—" no resto. */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Tenant</Typography>
                  <Typography variant="body2">—</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Correlação</Typography>
                  {/* O AuditoriaPublisher não grava correlationId no evento (lacuna). */}
                  <Typography variant="body2">— <Typography component="span" variant="caption" color="text.secondary">(não registrado pelo backend)</Typography></Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">IP</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {detalhe.ip ?? '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">User-Agent</Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}
                  >
                    {detalhe.userAgent ?? '—'}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="caption" color="text.secondary">
                Persistido em: {dayjs(detalhe.receivedAt).format('DD/MM/YYYY HH:mm:ss')}
              </Typography>

              <Box sx={{ textAlign: 'right' }}>
                <Button onClick={() => setDetalhe(null)}>Fechar</Button>
              </Box>
            </Stack>
          )}
        </Box>
      </Drawer>
    </Box>
  )
}

export default AuditoriaPage
